import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaFileSignature,
  FaBriefcase,
  FaPlay,
  FaHourglassHalf,
  FaBalanceScale,
  FaLayerGroup,
  FaBuilding,
  FaFlagCheckered,
  FaExternalLinkAlt,
  FaDownload,
} from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import { ouvrirPdf, telechargerPdf } from '../../components/Utils/ouvrirPdf';

/**
 * « Mon contrat » (Yros/Kavero#12) : le contrat en cours du salarié, lu en
 * lecture seule dans la Paie via GET /api/rh/contrat.
 *
 * Écran administratif : aucun montant, les chiffres vivent dans les bulletins.
 * Une donnée absente dans la Paie n'a pas de ligne — pas de « non renseigné »
 * qui inquiéterait le salarié pour un oubli de saisie côté RH.
 */

// Au-delà, une fin de contrat n'appelle encore aucune démarche.
const JOURS_ECHEANCE_PROCHE = 30;

const estDate = (valeur) => typeof valeur === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(valeur);

const dateLocale = (valeur) => {
  const [annee, mois, jour] = valeur.split('-').map(Number);
  return new Date(annee, mois - 1, jour);
};

const formaterDate = (valeur, langue) => {
  if (!estDate(valeur)) return null;
  return new Intl.DateTimeFormat(langue, { day: 'numeric', month: 'long', year: 'numeric' })
    .format(dateLocale(valeur));
};

/** Jours entiers d'aujourd'hui à `valeur`, négatif si la date est passée. */
const joursRestants = (valeur) => {
  const aujourdhui = new Date();
  const debutJour = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), aujourdhui.getDate());
  return Math.round((dateLocale(valeur) - debutJour) / 86400000);
};

function Echeance({ dateFin, langue }) {
  const { t } = useTranslation();
  const jours = joursRestants(dateFin);
  const proche = jours >= 0 && jours <= JOURS_ECHEANCE_PROCHE;
  let reste = null;
  if (proche) {
    reste = jours === 0 ? t('monContrat.echeanceAujourdhui') : t('monContrat.echeanceDans', { count: jours });
  }

  return (
    <div
      data-testid="mon-contrat-echeance"
      data-proche={String(proche)}
      className={`flex items-center gap-3 p-3 sm:p-4 rounded-2xl border ${
        proche ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-slate-50 border-slate-100 text-slate-700'
      }`}
    >
      <FaFlagCheckered className="w-4 h-4 shrink-0" aria-hidden="true" />
      <p className="text-sm font-semibold">
        {t('monContrat.echeance', { date: formaterDate(dateFin, langue) })}
        {reste && <span className="font-normal"> — {reste}</span>}
      </p>
    </div>
  );
}

// Le contrat signé déposé par la RH (Yros/Kavero#489) : l'écran reste en
// lecture seule, le salarié ne peut que l'ouvrir ou le télécharger.
function ContratSigne() {
  const { t } = useTranslation();
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(false);
  const nom = `${t('monContrat.nomFichier')}.pdf`;

  const agir = async (action) => {
    setEnCours(true);
    setErreur(false);
    try {
      await action(() => apiService.lireMonContratSigne(), nom);
    } catch (e) {
      signalerErreur(e, 'MonContrat.document');
      setErreur(true);
    } finally {
      setEnCours(false);
    }
  };

  const bouton = 'flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50';
  return (
    <div data-testid="mon-contrat-document" className="p-4 sm:p-5 rounded-2xl bg-white border border-slate-100 shadow-sm space-y-3">
      <p className="text-sm font-semibold text-slate-800">{t('monContrat.documentSigne')}</p>
      <div className="flex gap-2">
        <button type="button" data-testid="mon-contrat-ouvrir" disabled={enCours} onClick={() => agir(ouvrirPdf)}
          className={`${bouton} bg-[--color-primary] text-white hover:bg-[--bg-hover]`}>
          <FaExternalLinkAlt className="w-3.5 h-3.5" aria-hidden="true" />{t('monContrat.ouvrir')}
        </button>
        <button type="button" data-testid="mon-contrat-telecharger" disabled={enCours} onClick={() => agir(telechargerPdf)}
          className={`${bouton} border border-[--color-primary] text-[--color-primary] hover:bg-slate-50`}>
          <FaDownload className="w-3.5 h-3.5" aria-hidden="true" />{t('monContrat.telecharger')}
        </button>
      </div>
      {erreur && (
        <p role="alert" data-testid="mon-contrat-document-erreur" className="text-sm text-red-700">
          {t('monContrat.erreurDocument')}
        </p>
      )}
    </div>
  );
}

function MonContrat() {
  const { t, i18n } = useTranslation();
  const langue = i18n.resolvedLanguage?.startsWith('en') ? 'en-GB' : 'fr-FR';
  const [contrat, setContrat] = useState(null);
  const [erreur, setErreur] = useState(false);
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(false);
    try {
      const res = await apiService.getElement('/rh/contrat');
      if (res?.data?.ok) {
        setContrat(res.data.contrat || null);
      } else {
        setErreur(true);
      }
    } catch (e) {
      signalerErreur(e, 'MonContrat.charger');
      setErreur(true);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  if (chargement) {
    return (
      <div className="mt-8" data-testid="mon-contrat-chargement">
        <LoadingBarWithStyles />
      </div>
    );
  }

  // Le message se traduit au rendu : une bascule de langue ne le fige pas.
  if (erreur) {
    return (
      <div
        className="mt-6 p-4 sm:p-5 bg-red-50/90 border border-red-200/80 rounded-2xl text-red-700 text-center shadow-sm"
        data-testid="mon-contrat-erreur"
        role="alert"
      >
        <p className="font-semibold text-sm sm:text-base">{t('monContrat.erreurTechnique')}</p>
      </div>
    );
  }

  if (!contrat) {
    return (
      <div
        className="mt-6 p-5 sm:p-6 bg-white border border-slate-100 rounded-2xl text-center shadow-sm"
        data-testid="mon-contrat-vide"
      >
        <FaFileSignature className="w-8 h-8 mx-auto mb-3 text-slate-300" aria-hidden="true" />
        <p className="text-sm sm:text-base text-slate-600">{t('monContrat.vide')}</p>
      </div>
    );
  }

  const stage = contrat.nature === 'stage';
  const type = stage ? t('monContrat.typeStage') : contrat.type_contrat;
  const lignes = [
    { cle: 'poste', icone: FaBriefcase, label: t('monContrat.poste'), valeur: contrat.poste },
    { cle: 'date_debut', icone: FaPlay, label: t('monContrat.dateDebut'), valeur: formaterDate(contrat.date_debut, langue) },
    { cle: 'fin_essai', icone: FaHourglassHalf, label: t('monContrat.finEssai'), valeur: formaterDate(contrat.fin_essai, langue) },
    { cle: 'convention', icone: FaBalanceScale, label: t('monContrat.convention'), valeur: contrat.convention },
    { cle: 'categorie', icone: FaLayerGroup, label: t('monContrat.categorie'), valeur: contrat.categorie },
    { cle: 'depuis', icone: FaBuilding, label: t('monContrat.depuis'), valeur: formaterDate(contrat.depuis, langue) },
  ].filter((ligne) => ligne.valeur);

  return (
    <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-5" data-testid="mon-contrat-contenu">
      <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100 flex items-center gap-4">
        <span className="w-1 self-stretch rounded-full bg-[--color-primary] flex-shrink-0" />
        <div className="w-11 h-11 rounded-xl bg-slate-100 text-[--color-primary] flex items-center justify-center shrink-0">
          <FaFileSignature className="w-5 h-5" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          {/* Sans type saisi dans la Paie, le titre de l'écran tient lieu
              d'en-tête plutôt qu'un libellé « Type » suivi de rien. */}
          {type && (
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              {t('monContrat.type')}
            </p>
          )}
          <h2 className="text-lg sm:text-xl font-bold tracking-tight text-slate-800" data-testid="mon-contrat-type">
            {type || t('monContrat.titre')}
          </h2>
        </div>
      </div>

      {contrat.document && <ContratSigne />}

      {estDate(contrat.date_fin) && <Echeance dateFin={contrat.date_fin} langue={langue} />}

      {lignes.length > 0 && (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {lignes.map(({ cle, icone: Icone, label, valeur }) => (
            <div
              key={cle}
              data-testid={`mon-contrat-${cle}`}
              className="p-3 sm:p-4 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-start gap-3"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-500 shrink-0">
                <Icone className="w-3.5 h-3.5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <dt className="text-[10px] sm:text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
                  {label}
                </dt>
                <dd className="text-sm font-semibold text-slate-800 break-words">{valeur}</dd>
              </div>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export default MonContrat;
